import type { AxiosError } from 'axios';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  BrokerProfileResponseSchema,
  BrokerProfileUpdateResponseSchema,
} from 'src/schema/broker-profile-schema';
import type {
  TBrokerProfileResponse,
  TBrokerProfileUpdateBody,
} from 'src/types/broker-profile';

import { instance } from './instance';

// ----------------------------------------------------------------------

const BROKER_PROFILE_BASE = '/api/panel/core/v1.0/broker/profile';

/** GET /api/panel/core/v1.0/broker/profile/ — includes `data.time_zone` (or `data.timezone`) when set. */
const getBrokerProfile = async (): Promise<TBrokerProfileResponse> => {
  const { data } = await instance.get(`${BROKER_PROFILE_BASE}/`);
  const result = BrokerProfileResponseSchema.safeParse(data);
  if (!result.success) {
    throw new Error(`Parse broker profile: ${result.error.toString()}`);
  }
  return result.data;
};

type BrokerProfilePutResult = { message: string } & Record<string, unknown>;

const putBrokerProfile = async (
  body: TBrokerProfileUpdateBody,
): Promise<BrokerProfilePutResult> => {
  const { data } = await instance.put(`${BROKER_PROFILE_BASE}/`, body);
  const result = BrokerProfileUpdateResponseSchema.safeParse(data);
  if (!result.success) {
    throw new Error(`Parse broker profile update: ${result.error.toString()}`);
  }
  return result.data as BrokerProfilePutResult;
};

function useGetBrokerProfile({
  enabled = true,
}: { enabled?: boolean } = {}): UseQueryResult<
  TBrokerProfileResponse,
  AxiosError
> {
  return useQuery({
    queryKey: ['broker-profile'],
    queryFn: getBrokerProfile,
    enabled,
  });
}

function usePutBrokerProfile(): UseMutationResult<
  BrokerProfilePutResult,
  AxiosError,
  TBrokerProfileUpdateBody
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: putBrokerProfile,
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ['broker-profile'] });
      await queryClient.invalidateQueries({ queryKey: ['user-info'] });
    },
  });
}

export {
  getBrokerProfile,
  putBrokerProfile,
  useGetBrokerProfile,
  usePutBrokerProfile,
};
